import { useEffect, useRef, useCallback } from "react";
import { Reply, Smile, Pin, Pencil, Trash2 } from "lucide-react";
import { getInitials, getAvatarColor, formatTimestamp } from "@/mock/data";
import type { Message } from "@snowcord/shared";
import "./DMMessageList.css";

interface DMMessageListProps {
  messages: Message[];
  currentUserId: string;
  getUserDisplayName: (id: string) => string;
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onReply: (messageId: string) => void;
  onReact: (messageId: string) => void;
  onPin: (messageId: string) => void;
  onEdit: (messageId: string, content: string) => void;
  onDelete: (messageId: string) => void;
  onRetry: (tempId: string) => void;
}

export function DMMessageList({
  messages,
  currentUserId,
  getUserDisplayName,
  onLoadMore,
  hasMore,
  loadingMore,
  onReply,
  onReact,
  onPin,
  onEdit,
  onDelete,
  onRetry,
}: DMMessageListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore) {
          onLoadMore();
        }
      },
      { root: listRef.current, threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  let lastSenderId = "";
  let lastDate = "";
  const rendered: React.ReactNode[] = [];

  messages.forEach((msg) => {
    const date = new Date(msg.createdAt).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    if (date !== lastDate) {
      lastDate = date;
      lastSenderId = "";
      rendered.push(
        <div className="dm-message-list__date-divider" key={`date-${msg.id}`}>
          <div className="dm-message-list__date-line" />
          <span className="dm-message-list__date-label">{date}</span>
          <div className="dm-message-list__date-line" />
        </div>,
      );
    }

    const isGroupStart = msg.senderId !== lastSenderId;
    lastSenderId = msg.senderId;
    const isOwn = msg.senderId === currentUserId;
    const senderName = getUserDisplayName(msg.senderId);
    const isFailed = msg.status === "failed";

    rendered.push(
      <div
        className={`dm-message-list__group${isGroupStart ? " dm-message-list__group--first" : ""}`}
        key={msg.id}
        style={{ position: "relative" }}
      >
        {isGroupStart ? (
          <div
            className="dm-message-list__avatar"
            style={{ background: getAvatarColor(msg.senderId) }}
            title={senderName}
          >
            {getInitials(senderName)}
          </div>
        ) : (
          <div className="dm-message-list__avatar-spacer" />
        )}
        <div className="dm-message-list__body">
          {isGroupStart && (
            <div className="dm-message-list__header">
              <span className="dm-message-list__name">{senderName}</span>
              <span className="dm-message-list__time">
                {formatTimestamp(msg.createdAt)}
              </span>
            </div>
          )}
          <div className={`dm-message-list__text${isFailed ? " dm-message-list__text--failed" : ""}`}>
            {msg.content}
          </div>
          {isFailed && (
            <span
              className="dm-message-list__retry-btn"
              onClick={() => onRetry(msg.id)}
              role="button"
              tabIndex={0}
            >
              Retry
            </span>
          )}
        </div>

        {isGroupStart && (
          <div className="dm-message-list__actions">
            <button
              className="dm-message-list__action-btn"
              onClick={() => onReply(msg.id)}
              aria-label="Reply"
            >
              <Reply size={14} />
            </button>
            <button
              className="dm-message-list__action-btn"
              onClick={() => onReact(msg.id)}
              aria-label="React"
            >
              <Smile size={14} />
            </button>
            <button
              className="dm-message-list__action-btn"
              onClick={() => onPin(msg.id)}
              aria-label="Pin"
            >
              <Pin size={14} />
            </button>
            {isOwn && (
              <>
                <button
                  className="dm-message-list__action-btn"
                  onClick={() => onEdit(msg.id, msg.content)}
                  aria-label="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  className="dm-message-list__action-btn"
                  onClick={() => onDelete(msg.id)}
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </div>
        )}
      </div>,
    );
  });

  return (
    <div className="dm-message-list" ref={listRef}>
      {hasMore && (
        <div ref={topSentinelRef} className="dm-message-list__loader">
          {loadingMore ? "Loading..." : "Load more"}
        </div>
      )}
      {rendered}
    </div>
  );
}
